// addFilter — VQE-Edit edit-action: append a WHERE (or, with `options.clause`,
// HAVING) predicate, synthesizing the clause if none exists. Builds an SR-Edit
// `add-where` / `add-having` op and delegates to `commitEdit`.
//
// PRD: Ralph/VisualQueryEditorEdit/PRD.md § Slice: Edit — add/modify/remove
// filter (WHERE / HAVING).

import type { WhereCombinator } from '../../../../sql-resolver/types';
import type { BooleanClauseTarget } from '../../../../sql-resolver/types/structuralEdit';
import { commitEdit, type CommitEditCtx, type CommitOutcome } from '../commitEdit';
import { assertWhereCombinator, formatFilterText, type FilterFormValue } from './filterHelpers';

export type AddFilterOptions = {
  /**
   * `'AND'` (default) | `'OR'`. Ignored when synthesizing a fresh clause — SR-Edit
   * always emits ` WHERE <text>` / ` HAVING <text>` for the empty case.
   */
  readonly combinator?: WhereCombinator;
  /** Target clause — `'where'` (default) or `'having'`. */
  readonly clause?: BooleanClauseTarget;
};

/**
 * Append a predicate to the WHERE (or HAVING) clause. The first argument is
 * either the literal predicate text (used by the inline-edit path and tests)
 * OR a structured `FilterFormValue` (used by the FilterBuilder popover) which
 * is stringified via `formatFilterText`.
 */
export async function addFilter(
  predicate: string | FilterFormValue,
  ctx: CommitEditCtx,
  options: AddFilterOptions = {},
): Promise<CommitOutcome> {
  // Wrapper pre-validation still THROWS before `commitEdit` on invalid input
  // (empty text / bad combinator) — that contract is unchanged (P12 decision 3),
  // and it never reaches commitEdit's failure recorder, so callers keep a
  // `.catch` for it. The commit itself no longer rejects; its `CommitOutcome`
  // is forwarded so outcome-dependent callers can branch on `status`.
  const combinator = assertWhereCombinator(options.combinator ?? 'AND');
  const text = typeof predicate === 'string' ? predicate.trim() : formatFilterText(predicate);
  if (text.length === 0) {
    throw new Error('addFilter: predicate text is empty');
  }
  const op = options.clause === 'having' ? 'add-having' : 'add-where';
  return commitEdit({ op, text, combinator }, ctx);
}
